import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Money, Btn } from "@/app/ui";
import { Plus, Trash2 } from "lucide-react";

/* Shared line-item editor for quotes & invoices */
export default function DocumentEditor({ initial, onSave, saving, kind = "Quote" }) {
  const { user } = useAuth();
  const sym = user?.business?.currency || "£";
  const defaultVat = user?.business?.vat_rate ?? 23;

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerId, setCustomerId] = useState(initial?.customer_id || "");
  const [items, setItems] = useState(initial?.line_items?.length ? initial.line_items : [{ description: "", quantity: 1, unit_price: 0, vat_rate: defaultVat }]);
  const [notes, setNotes] = useState(initial?.notes || "");
  const [terms, setTerms] = useState(initial?.terms || "");

  useEffect(() => {
    api.get("/customers?limit=200").then((r) => setCustomers(r.data.items));
    api.get("/products").then((r) => setProducts(r.data.items));
  }, []);

  const updateItem = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addItem = () => setItems([...items, { description: "", quantity: 1, unit_price: 0, vat_rate: defaultVat }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const pickProduct = (i, pid) => {
    const p = products.find((x) => x.id === pid);
    if (p) setItems(items.map((it, idx) => idx === i ? { ...it, description: p.name, unit_price: p.unit_price, vat_rate: p.vat_rate } : it));
  };

  const subtotal = items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);
  const vat = items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.unit_price || 0) * Number(it.vat_rate || 0) / 100, 0);
  const total = subtotal + vat;

  const submit = () => {
    if (!customerId) return;
    onSave({
      customer_id: customerId,
      line_items: items.map((it) => ({ description: it.description, quantity: Number(it.quantity), unit_price: Number(it.unit_price), vat_rate: Number(it.vat_rate) })),
      notes, terms,
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium text-slate-700">Customer *</label>
        <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} data-testid="doc-customer-select" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 bg-white">
          <option value="">Select a customer…</option>
          {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</option>)}
        </select>
      </div>

      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <div className="hidden sm:grid grid-cols-12 gap-2 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-500">
          <span className="col-span-5">Description</span><span className="col-span-2 text-right">Qty</span><span className="col-span-2 text-right">Unit</span><span className="col-span-1 text-right">VAT%</span><span className="col-span-2 text-right">Amount</span>
        </div>
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-slate-100 items-center" data-testid={`doc-line-${i}`}>
            <div className="col-span-12 sm:col-span-5">
              <input value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} placeholder="Item description" className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
              {products.length > 0 && <select onChange={(e) => pickProduct(i, e.target.value)} value="" className="mt-1 text-xs text-blue-accent bg-transparent"><option value="">+ from catalogue</option>{products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>}
            </div>
            <input type="number" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className="col-span-4 sm:col-span-2 rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-right font-mono" />
            <input type="number" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} className="col-span-4 sm:col-span-2 rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-right font-mono" />
            <input type="number" value={it.vat_rate} onChange={(e) => updateItem(i, "vat_rate", e.target.value)} className="col-span-2 sm:col-span-1 rounded-lg border border-slate-300 px-2 py-1.5 text-sm text-right font-mono" />
            <div className="col-span-2 flex items-center justify-end gap-1">
              <Money value={Number(it.quantity || 0) * Number(it.unit_price || 0)} symbol={sym} className="text-sm hidden sm:inline" />
              <button onClick={() => removeItem(i)} className="text-slate-300 hover:text-red-600"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
        <button onClick={addItem} data-testid="doc-add-line" className="w-full flex items-center justify-center gap-1 py-2 text-sm text-blue-accent hover:bg-blue-50 border-t border-slate-100"><Plus size={15} /> Add line</button>
      </div>

      <div className="flex justify-end">
        <div className="w-full sm:w-64 space-y-1 text-sm">
          <div className="flex justify-between text-slate-600"><span>Subtotal</span><Money value={subtotal} symbol={sym} /></div>
          <div className="flex justify-between text-slate-600"><span>VAT</span><Money value={vat} symbol={sym} /></div>
          <div className="flex justify-between font-semibold text-navy text-base border-t border-slate-200 pt-1"><span>Total</span><Money value={total} symbol={sym} /></div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="text-sm font-medium text-slate-700">Notes</label><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" /></div>
        <div><label className="text-sm font-medium text-slate-700">Terms</label><textarea value={terms} onChange={(e) => setTerms(e.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" /></div>
      </div>

      <Btn variant="accent" className="w-full" onClick={submit} disabled={saving || !customerId} data-testid="doc-save-button">{saving ? "Saving…" : `Save ${kind}`}</Btn>
    </div>
  );
}
